import invariant from 'invariant';

import config from 'Config';
import fetch from 'Lib/utils/fetch';

import { getLatitude, getLongitude } from '../location/selectors';
import { getProfileUserId } from '../profile/selectors';
import { getCommonData } from './lib/dataUtils';
import { LOGOUT_START } from './sparks.events';

const DEFAULT_BATCH_SIZE = 10;
const AUTO_FLUSH_INTERVAL = 10000;
const LOCATION_DELAY_INTERVAL = 30000;

class SparksService {
  constructor() {
    this.reset();
  }

  reset() {
    this._buffer = [];
    this._requireLocation = true;
    this.endpoint = config.sparks.endpoint;
  }

  init(store) {
    this._store = store;
    this.startAutoFlushing();
  }

  getStore = () => this._store;

  startAutoFlushing() {
    invariant(!this.timerId, 'Timer already started');
    this.timerId = setInterval(this.flush, AUTO_FLUSH_INTERVAL);
  }

  stopAutoFlushing() {
    if (!this.timerId) {
      return;
    }
    clearInterval(this.timerId);
    this.timerId = null;
  }

  resetAutoFlushingInterval() {
    this.stopAutoFlushing();
    this.startAutoFlushing();
  }

  track(eventData, options = { forceSend: false }) {
    if (!eventData) return;

    this._buffer.push(eventData);

    // Don't try to flush events until:
    // we have a logged in user with lat/lon & the buffer is big enough,  or the user is leaving the page
    if (!options.forceSend) {
      if (this._buffer.length < DEFAULT_BATCH_SIZE) return;
      if (!eventData.event.uid || !eventData.event.lat) return;
    }

    this.resetAutoFlushingInterval();
    this.flushBuffer();
  }

  flush = () => {
    const state = this._store.getState();
    const uid = getProfileUserId(state);
    let canFlush = !!uid;

    // Make sure we have location, if we need it
    if (this._requireLocation) {
      const lat = getLatitude(state);
      const lon = getLongitude(state);
      if (!lat || !lon) {
        canFlush = false;
      }
    }

    if (canFlush) {
      this.flushBuffer();
      clearTimeout(this.locationDelayId);
      this.locationDelayId = null;
    } else if (!this.locationDelayId) {
      this.locationDelayId = setTimeout(() => {
        this._requireLocation = false;
        this.locationDelayId = null;
        this.flushBuffer();
      }, LOCATION_DELAY_INTERVAL);
    }
  };

  flushBuffer() {
    // Start accepting new events, but keep current buffer around in case it fails
    let oldBuffer = this._buffer;
    this._buffer = [];

    const commonData = getCommonData(this._store.getState());
    const backfilledBuffer = oldBuffer.reduce((acc, item) => {
      // Don't backfill if one of the events was from logout (subsequent events could
      // be from a different user)
      if (!acc) return acc;
      if (item.schema === LOGOUT_START) return null;

      const newItem = { schema: item.schema, event: { ...item.event } };

      newItem.event.uid = item.event?.uid ?? commonData.uid;
      newItem.event.lat = item.event?.lat ?? commonData.lat;
      newItem.event.lon = item.event?.lon ?? commonData.lon;
      newItem.event.age = item.event?.age ?? commonData.age;
      newItem.event.gender = item.event?.gender ?? commonData.gender;

      return acc.concat(newItem);
    }, []);

    if (backfilledBuffer) oldBuffer = backfilledBuffer;

    if (!oldBuffer || !oldBuffer.length) {
      // skip empty buffer
      return;
    }
    this.sendBatch(oldBuffer).catch(() => {
      this._buffer = oldBuffer.concat(this._buffer);
    });
  }

  sendBatch(eventBuffer) {
    const options = {
      method: 'PUT',
      body: JSON.stringify({ events: eventBuffer }),
      headers: { 'Content-Type': 'application/json' },
    };

    return fetch(this.endpoint, options);
  }
}

export default new SparksService();
